import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { parseGaraazSlotNumber } from "../../../components/DeliveryOptions/utils/deliveryTimeUtils";
import { CPTSlabForManualOption } from "../enums";

dayjs.extend(utc);
dayjs.extend(timezone);

export const IST_TIMEZONE = "Asia/Kolkata";
const HH_MM_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export type GaraazSlotPickerWindow = {
  slotId: number;
  startTime: string;
  endTime: string;
};

export function parseSlotClock(
  value: string | undefined,
): { hours: number; minutes: number } | null {
  if (!value || typeof value !== "string") {
    return null;
  }
  const match = HH_MM_PATTERN.exec(value.trim());
  if (!match) {
    return null;
  }
  return {
    hours: parseInt(match[1], 10),
    minutes: parseInt(match[2], 10),
  };
}

export function normalizeIstDateString(dateIst: string): string | null {
  const trimmed = dateIst?.trim() ?? "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }
  const parsed = dayjs.tz(`${trimmed} 00:00:00`, IST_TIMEZONE);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

function instantOnIstDate(
  dateIst: string,
  clock: string,
): dayjs.Dayjs | null {
  const date = normalizeIstDateString(dateIst);
  const parts = parseSlotClock(clock);
  if (!date || !parts) {
    return null;
  }
  const hh = String(parts.hours).padStart(2, "0");
  const mm = String(parts.minutes).padStart(2, "0");
  const instant = dayjs.tz(`${date} ${hh}:${mm}:00`, IST_TIMEZONE);
  return instant.isValid() ? instant : null;
}

/** CPT datetime: selected IST date + slot endTime → YYYY-MM-DDTHH:mm:ss */
export function etaFromDateAndSlot(
  dateIst: string,
  slot: Pick<GaraazSlotPickerWindow, "endTime">,
): string | null {
  const instant = instantOnIstDate(dateIst, slot.endTime);
  return instant ? instant.format("YYYY-MM-DDTHH:mm:ss") : null;
}

export type OrderEtaSlotPayload = {
  minGaraazSlot: number;
  minGaraazSlotStartTime: string;
  minGaraazSlotEndTime: string;
  maxGaraazSlot: number;
  maxGaraazSlotStartTime: string;
  maxGaraazSlotEndTime: string;
};

/** Full IST slot window fields to stamp on orderETA with CPT. */
export function orderEtaSlotFieldsFromDateAndSlot(
  dateIst: string,
  slot: GaraazSlotPickerWindow,
): OrderEtaSlotPayload | null {
  const start = instantOnIstDate(dateIst, slot.startTime);
  const end = instantOnIstDate(dateIst, slot.endTime);
  if (!start || !end) {
    return null;
  }
  const startTime = start.format("YYYY-MM-DDTHH:mm:ss");
  const endTime = end.format("YYYY-MM-DDTHH:mm:ss");
  return {
    minGaraazSlot: slot.slotId,
    minGaraazSlotStartTime: startTime,
    minGaraazSlotEndTime: endTime,
    maxGaraazSlot: slot.slotId,
    maxGaraazSlotStartTime: startTime,
    maxGaraazSlotEndTime: endTime,
  };
}

export function parseGaraazSlotPickerWindows(
  raw: unknown,
): GaraazSlotPickerWindow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const windows: GaraazSlotPickerWindow[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const slotId = parseGaraazSlotNumber(
      (entry as { slotId?: unknown }).slotId,
    );
    const startTime = String(
      (entry as { startTime?: unknown }).startTime ?? "",
    ).trim();
    const endTime = String(
      (entry as { endTime?: unknown }).endTime ?? "",
    ).trim();
    if (slotId == null || !parseSlotClock(startTime) || !parseSlotClock(endTime)) {
      continue;
    }
    windows.push({ slotId, startTime, endTime });
  }
  return windows.sort((a, b) => a.slotId - b.slotId);
}

/**
 * Slots for the chosen calendar date. If that date is today (IST), drop
 * windows whose start is already at or before now.
 */
export function slotsSelectableOnDate(
  slots: GaraazSlotPickerWindow[],
  dateIst: string,
  now: dayjs.Dayjs = dayjs(),
): GaraazSlotPickerWindow[] {
  const date = normalizeIstDateString(dateIst);
  if (!date) {
    return [];
  }
  const nowIst = now.tz(IST_TIMEZONE);
  const todayIst = nowIst.format("YYYY-MM-DD");
  const isToday = date === todayIst;

  return parseGaraazSlotPickerWindows(slots).filter((slot) => {
    if (!isToday) {
      return true;
    }
    const start = instantOnIstDate(date, slot.startTime);
    if (!start) {
      return false;
    }
    return start.isAfter(nowIst);
  });
}

const DEFAULT_SLOT_DATE_ROLL_DAYS = 14;

/**
 * First IST calendar day (from today) that still has at least one selectable slot.
 * Used when today's slots have all started — advance to tomorrow instead of an empty list.
 */
export function findEarliestDateWithSelectableSlots(
  slots: GaraazSlotPickerWindow[],
  now: dayjs.Dayjs = dayjs(),
  maxDayRoll: number = DEFAULT_SLOT_DATE_ROLL_DAYS,
): string | null {
  const windows = parseGaraazSlotPickerWindows(slots);
  if (!windows.length) {
    return null;
  }
  const nowIst = now.tz(IST_TIMEZONE);
  for (let offset = 0; offset < maxDayRoll; offset += 1) {
    const dateIst = nowIst.add(offset, "day").format("YYYY-MM-DD");
    if (slotsSelectableOnDate(windows, dateIst, nowIst).length > 0) {
      return dateIst;
    }
  }
  return null;
}

export function isStandardDeliveryCptSlab(cptSlab: unknown): boolean {
  if (typeof cptSlab === "number") {
    return cptSlab === CPTSlabForManualOption.STANDARD_DELIVERY;
  }
  if (cptSlab && typeof cptSlab === "object" && "slabLevel" in cptSlab) {
    return (
      Number((cptSlab as { slabLevel?: unknown }).slabLevel) ===
      CPTSlabForManualOption.STANDARD_DELIVERY
    );
  }
  return false;
}

/**
 * System Standard Delivery stamps minGaraazSlot on orderETA.
 * Manual "+ ADD OPTION" Standard Delivery does not — keep date+time for those.
 */
export function orderHasSystemGaraazSlotCpt(orderEta: unknown): boolean {
  if (!Array.isArray(orderEta)) {
    return false;
  }
  return orderEta.some(
    (entry) => parseGaraazSlotNumber(entry?.minGaraazSlot) != null,
  );
}

function idString(value: unknown): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (typeof value === "object" && value !== null && "_id" in value) {
    return idString((value as { _id?: unknown })._id);
  }
  if (typeof value === "object" && typeof (value as { toString?: () => string }).toString === "function") {
    const asString = (value as { toString: () => string }).toString();
    if (asString && asString !== "[object Object]") {
      return asString;
    }
  }
  return undefined;
}

export function resolveStandardDeliveryPickupSupplierId(order: {
  customer?: {
    pickupPoints?: Array<{ pickupPointId?: unknown; isDefault?: boolean }>;
  };
  allocatedTo?: Array<{ isActive?: boolean; nextSupplierId?: unknown }>;
} | null | undefined): string | undefined {
  const pickupPoints = order?.customer?.pickupPoints;
  if (Array.isArray(pickupPoints) && pickupPoints.length > 0) {
    const defaultPoint = pickupPoints.find((point) => point?.isDefault);
    const chosen = defaultPoint ?? pickupPoints[0];
    const fromPickup = idString(chosen?.pickupPointId);
    if (fromPickup) {
      return fromPickup;
    }
  }

  const allocations = order?.allocatedTo;
  if (!Array.isArray(allocations)) {
    return undefined;
  }
  const active =
    allocations.find((row) => row?.isActive) ?? allocations[0];
  return idString(active?.nextSupplierId);
}

export function readSupplierGaraazSlots(
  suppliers: Array<{
    _id?: unknown;
    deliveryTerms?: { garaazSlots?: unknown };
  }> | undefined,
  supplierId: string | undefined,
): GaraazSlotPickerWindow[] {
  if (!supplierId || !Array.isArray(suppliers)) {
    return [];
  }
  const supplier = suppliers.find(
    (entry) => idString(entry?._id) === supplierId,
  );
  return parseGaraazSlotPickerWindows(supplier?.deliveryTerms?.garaazSlots);
}

export function getStandardDeliverySlotPickerProps(
  order: {
    cptSlab?: unknown;
    orderETA?: unknown;
    customer?: {
      pickupPoints?: Array<{ pickupPointId?: unknown; isDefault?: boolean }>;
    };
    allocatedTo?: Array<{ isActive?: boolean; nextSupplierId?: unknown }>;
  } | null | undefined,
  suppliers:
    | Array<{
        _id?: unknown;
        deliveryTerms?: { garaazSlots?: unknown };
      }>
    | undefined,
): {
  useGaraazSlots: boolean;
  garaazSlots: GaraazSlotPickerWindow[];
} {
  const useGaraazSlots =
    isStandardDeliveryCptSlab(order?.cptSlab) &&
    orderHasSystemGaraazSlotCpt(order?.orderETA);
  if (!useGaraazSlots) {
    return { useGaraazSlots: false, garaazSlots: [] };
  }
  return {
    useGaraazSlots: true,
    garaazSlots: readSupplierGaraazSlots(
      suppliers,
      resolveStandardDeliveryPickupSupplierId(order),
    ),
  };
}

